/*
    Adapts the engagement budget tree onto the segmented screen's shapes.

    The server already models segments (PaceSegment) and resolves per-segment network splits,
    so a campaign that has paces renders from real data. One that has none comes back as a
    single synthetic segment with null bounds — that is the un-migrated case, and it seeds an
    Even segment across the flight instead.
*/

import socialMediaData from "src/shared/data/socialMediaData"
import type {
  BudgetTreeAdGroup,
  BudgetTreeLaunch,
  BudgetTreeNetwork,
} from "@models/engagementBudgetTree.model"
import { agKey } from "./floors"
import { Flight, Segment, seg } from "./segmentEngine"
import { SegmentNetwork } from "./SegmentNetworksTable"

const GLYPH: Record<string, string> = {
  meta: "f",
  facebook: "f",
  google: "G",
  snapchat: "S",
  pinterest: "P",
  linkedin: "in",
  tiktok: "t",
}

const isoDay = (v: string): string => v.slice(0, 10)

export const toSegmentNetworks = (networks: BudgetTreeNetwork[]): SegmentNetwork[] =>
  networks.map((n) => {
    const key = n.network.toLowerCase()
    const display = socialMediaData[key]
    return {
      key,
      name: display?.label ?? n.network,
      isConfigured: n.is_configured !== false,
      active: !n.is_paused,
      color: display?.color ?? "#667eea",
      glyph: GLYPH[key] ?? n.network.slice(0, 1).toUpperCase(),
      dark: key === "snapchat",
      strategy: n.bid_strategy ?? "",
      bid: n.bid != null ? `$${n.bid.toFixed(2)}` : "—",
      bidUnsupported: n.accepts_bid === false,
      dailyCap: n.daily_spend_cap != null ? n.daily_spend_cap.toFixed(2) : "—",
      dailyCapAmount: n.daily_spend_cap ?? null,
      bidAmount: n.bid ?? null,
      // The network's own serving days. A network that narrows the campaign's schedule has
      // fewer days to spend its share over, so its daily rate is higher (MPN-1802).
      scheduledRanges: n.scheduled_ranges,
      adGroups: n.ad_groups.map((a) => ({
        key: String(a.ad_group_id),
        name: a.audience_name || a.ad_group_name,
        // The tree carries each ad group's own resolved share of its network.
        share: n.resolved_amount > 0 ? a.resolved_amount / n.resolved_amount : 0,
        percentOfNetwork: a.percent,
        published: true,
        bidLabel: n.bid_strategy ?? undefined,
        bidValue: a.bid != null ? `$${a.bid.toFixed(2)}` : undefined,
        bidAmount: a.bid ?? null,
        // The campaign-wide answer, and its own toggle rather than its network's: a row
        // greyed because its network is off is the cascade, which the table already draws
        // from the network's own state. A window has its own `agOn`, which
        // `adGroupOnIn` consults first.
        on: !a.has_own_pause,
      })),
    }
  })

/** Campaign-default mix: each network's share of the launch, before any per-segment pin. */
export const toDefaultMix = (launch: BudgetTreeLaunch): Record<string, number> => {
  const out: Record<string, number> = {}
  launch.networks
    .filter((n) => n.is_configured !== false)
    .forEach((n) => {
      out[n.network.toLowerCase()] = n.percent
    })
  return out
}

/**
 * The campaign's own dates.
 *
 * Its `campaign_start` / `campaign_end` first — the LAUNCH schedule entry Ops edit, which
 * MPN-2040 promotes to the single source of truth. The screen labels these "Campaign end"
 * under a padlock, so borrowing a different range here would make a wrong date look
 * authoritative.
 *
 * Then the campaign's own spend windows, then the engagement's window as a last resort for
 * a campaign that has no dates at all — 113 of 195 launches, all staging or closed.
 */
export const toFlight = (launch: BudgetTreeLaunch, fallback: Flight): Flight => {
  if (launch.campaign_start && launch.campaign_end) {
    return { start: launch.campaign_start, end: launch.campaign_end }
  }
  const dated = launch.pace_segments.filter((s) => s.start_time && s.end_time)
  if (!dated.length) return fallback
  return {
    start: dated.map((s) => isoDay(s.start_time as string)).sort()[0],
    end: dated
      .map((s) => isoDay(s.end_time as string))
      .sort()
      .slice(-1)[0],
  }
}

/** Dollars-per-segment, from the server's own resolved amounts where segments exist. */
export const toSegments = (launch: BudgetTreeLaunch, flight: Flight): Segment[] => {
  const dated = launch.pace_segments.filter(
    (segment) => segment.start_time && segment.end_time,
  )
  if (!dated.length) return [seg(flight.start, flight.end, launch.resolved_amount)]

  return dated
    .slice()
    .sort((earlier, later) =>
      (earlier.start_time as string).localeCompare(later.start_time as string),
    )
    .map((segment) =>
      seg(
        isoDay(segment.start_time as string),
        isoDay(segment.end_time as string),
        segment.resolved_amount,
        {
          // Carry the server's own id. Without it the engine mints a fresh one on every
          // load, every window looks new to Save, and the second save of a campaign
          // collides with the windows it is replacing.
          id: String(segment.segment_id),
          // A per-segment split that differs from the campaign default is a manual pin.
          pinned: Object.fromEntries(
            segment.networks
              .filter((network) => network.has_explicit_policy)
              .map((network) => [network.network.toLowerCase(), network.percent]),
          ),
          /*
              Pause is per window (MPN-1893), so it is carried here rather than on the
              campaign-wide network list the table is built from.

              EVERY network is recorded, on or off, not just the paused ones. The list the
              table is built from is `launchNetworks`, which aggregates the segments and
              keeps the FIRST segment's `is_paused` -- so leaving a key out falls back to
              window 1's answer, and a network paused in window 1 and running in window 2
              read as paused in both. An explicit entry per window is the only state that
              cannot be misread.

              A network switched off for the whole campaign still reads as paused
              everywhere: the server ORs the campaign switch into every segment's
              `is_paused`, so each window reports it on its own.
          */
          netOn: Object.fromEntries(
            segment.networks.map((network) => [
              network.network.toLowerCase(),
              !network.is_paused,
            ]),
          ),
          /*
              The ad group's OWN toggle, never the cascade. `is_paused` on an ad group is the
              effective answer -- its own pause OR its network's -- and recording that would
              leave every ad group under a paused network stuck off after the network
              resumed. `has_own_pause` is the half that belongs to the ad group, and the
              screen recomputes the cascade from the network's own state.
          */
          agOn: Object.fromEntries(
            segment.networks.flatMap((network) =>
              network.ad_groups.map((adGroup) => [
                adGroupKey(network.network, adGroup),
                !adGroup.has_own_pause,
              ]),
            ),
          ),
        },
      ),
    )
}

/** The key `floors.ts` toggles an ad group under. Shares `agKey` so the two cannot drift. */
const adGroupKey = (network: string, adGroup: BudgetTreeAdGroup): string =>
  agKey({ key: network.toLowerCase() }, { key: String(adGroup.ad_group_id) })

/*
    Allocation — the campaign's slice of the engagement before margin, which is what the
    Amount note and the Margin tooltip are both stated against (MPN-1801).

    Taken from the server rather than recomputed. This used to be
    `remaining_flight_funding x percent` on this side, which lands on the same number today
    but is a second calculation of it: the server splits from `balance`, and the two stop
    being equal once MPN-1789 lands. The screen says "Amount = Allocation x (1 - Margin)",
    so both halves have to come from one place or the sentence is only accidentally true.
*/
export const toAllocation = (launch: BudgetTreeLaunch): number => launch.allocation

export const hasSegments = (launch: BudgetTreeLaunch): boolean =>
  launch.pace_segments.some((s) => s.start_time && s.end_time)

/** The campaign's serving ranges, before any per-network narrowing. */
export const toScheduledRanges = (launch: BudgetTreeLaunch): [string, string][] =>
  launch.scheduled_ranges
